/* eslint-disable react/no-unescaped-entities */
import { Meta } from '@/layout/Meta'
import { Main } from '@/templates/Main'
import { AppConfig } from '@/utils/AppConfig'
import { Code } from '@/utils/Code'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { note } from '@/utils/Note'
import { useEffect } from 'react'
import BlogCta from './blog-cta'

const GaLink = (props: {path: string, children: string}) => {
  const relativePath = props.path.replace(/^\//, '') // strip leading slash if it exists
  const linkText = props.children
  return (
    <Link href={`https://developers.google.com/google-ads/${relativePath}`}>
      { ' ' + linkText }
    </Link>
  )
}

const GcLink = (props: {path: string, children: string}) => {
  const relativePath = props.path.replace(/^\//, '') // strip leading slash if it exists
  const linkText = props.children
  return (
    <Link href={`https://console.cloud.google.com/${relativePath}`}>
      { linkText }
    </Link>
  )
}

const HowToIntegrateWithGoogleAdsApi = () => {
  const router = useRouter()

  useEffect(() => {
    note('Visited Google Ads tutorial')
  }, [])
  
  return (
    <Main meta={<Meta 
        title="How to Integrate with Google Ads Rest API"
        description="Connecting to Google Ads Rest API" />}>
      <div>
        <h1 className="text-4xl font-bold text-sky-700">Google Ads</h1>
        <h2>Criteria Metrics</h2>
        <BlogCta />
          <h2 className="text-4xl font-bold text-sky-700">Overview</h2>
          <GaLink path="/api/rest/overview">Google Ads</GaLink> provides well documented APIs for tracking your ad performance.  They
          are medium difficulty to get started, but very robust once you are running.  Google recommends using the
          <GaLink path="/api/docs/client-libs"> client libraries</GaLink>, but this blog post will cover the 
          <GaLink path="/api/rest/overview">REST API</GaLink>.

        <p>
          <strong>Note -</strong> the HTTP requests below are compatible with the <Link href="https://marketplace.visualstudio.com/items?itemName=humao.rest-client">
            VSCode Rest Client</Link>.  I find this extension easier than Postman because you can check-in your requests with your code.
          Parameters like <code className="text-sky-700"> {'{{ $dotenv SOME_ID }}'} </code> are referencing environment variables from 
          a .env file.
        </p>

        <p>
          <strong>Alsø Alsø note -</strong> this post goes into details for setting up your Google Ads accounts.  If you just want the code,
          then scroll to the bottom.
        </p>
      </div>
      <div>
        <h2 className="text-xl font-bold text-sky-700">Prerequisites</h2>
        Google requires you sto start with <GaLink path="/api/docs/first-call/test-accounts">sandbox accounts</GaLink>.  This holds for 
        your OAuth app as well.
      </div>
      <div>
        <strong>The steps are:</strong>
        <ol className="list-decimal list-inside">
          <li>Get a developer token</li>
          <li>Setup an OAuth refresh token</li>
          <li>Identify your customer ID and manager ID</li>
          <li>Query the Google Ads Search API</li>
        </ol>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Get a developer token</h2>
        To <GaLink path="/api/docs/first-call/dev-token">obtain a developer token</GaLink>, you will first need a manager
        account linked to your Google Ads account.  Once you have them linked, you will see an 
        <Link href="https://ads.google.com/aw/apicenter"> API Center</Link> option appear in your Tools and Settings options.

        <img className="h-64 m-auto pt-10" src={`${router.basePath}/assets/images/google-ads-api-center-location.png`} alt="CJ Business ID" />
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Setup an OAuth refresh token</h2>
        Log into <GcLink path="/">Google Cloud</GcLink>, setup an <GcLink path="/apis/credentials/consent">OAuth Consent Screen</GcLink>, and 
        then <GcLink path="/apis/credentials">create OAuth credentials</GcLink>.

        The setup is application specific, below are examples for { AppConfig.domain}
        
        <div>
          <strong>OAuth Consent</strong>
          <ul className="pt-0 mt-0 list-disc list-inside">
            <li>Test users: <code>[your google cloud email]</code></li>
            <li>Authorized Domain: <code>{ AppConfig.domain }</code></li>
          </ul>
        </div>
        <div>
          <strong>Credentials</strong>
          <ul className="list-disc list-inside">
            <li>OAuth type: <code>OAuth 2.0 Client IDs</code></li>
            <li>Application type: <code>Web application</code></li>
            <li>Authorized Javascript Origins: <code>[https://{ AppConfig.domain }, http://localhost]</code></li>
            <li>Authorized Redirect URLs: <code>[https://{ AppConfig.domain }, http://localhost]</code></li>
          </ul>
        </div>

        <p>
          <strong>Note - </strong> write down your OAuth Client ID and Client Secret.  You will need them in the next step.
        </p>

        <div>
          <h2 className="text-xl font-bold text-sky-700">Request your initial OAuth code</h2>
          The first step in the OAuth flow is to get an authorization code from the client.  You do this by crafting
          a URL and opening it in a browser.

          <Code>
            {`
            // Remove newlines
            GET https://accounts.google.com/o/oauth2/v2/auth
                ?scope=https://www.googleapis.com/auth/adwords
                &access_type=offline
                &include_granted_scopes=true
                &response_type=code
                &state=anything
                &redirect_uri=http://localhost
                &client_id={{ $dotenv GA_CLIENT_ID }}
            `}
          </Code>

          <p>
            Once you authorize your client, you will be redirected to a page that seems broken, but the URL will contain
            a code parameter.  Copy that code and use it to get a refresh token.
          </p>

          <Code>
            http://localhost/?state=anything&code=[YOUR CODE]&scope=...
          </Code>

          <h2 className="text-xl font-bold mt-5 text-sky-700">Get your refresh token</h2>
          <p>
            Using the [YOUR CODE] value from the above URL, you can craft an API call to get an access token, and more 
            importantly refresh token.
          </p>

          <Code>
            {`
            POST https://oauth2.googleapis.com/token HTTP/1.1
            Content-Type: application/x-www-form-urlencoded
            
            code=[YOUR CODE]
            &client_id={{ $dotenv GA_CLIENT_ID }}
            &client_secret={{ $dotenv GA_CLIENT_SECRET }}
            &redirect_uri=http://localhost
            &grant_type=authorization_code
            `}
          </Code>

          <div>
            <h2 className="text-xl font-bold mt-5 text-sky-700">Your access token</h2>
            You will need to an access token for future requests, you use your refresh token to retrieve one. 
            <Code>
              {`
              POST https://www.googleapis.com/oauth2/v3/token HTTP/1.1
              Content-Type: application/x-www-form-urlencoded
              
              grant_type=refresh_token
              &client_id={{ $dotenv GA_CLIENT_ID }}
              &client_secret={{ $dotenv GA_CLIENT_SECRET }}
              &refresh_token={{ $dotenv GA_REFRESH_TOKEN }}
              `}
            </Code>
          </div>
        </div>
        <div>
          <h2 className="text-4xl font-bold text-sky-700">Identify your customer ID and manager ID</h2>
          The final thing you will need is the customer ID and manager ID
          from your test accounts.  These are available in the Google Ads UI.

          <img className="h-64 m-auto pt-10" src={`${router.basePath}/assets/images/google-ads-customer-id.png`} alt="Google Ads Manager and Customer ID" />
        </div>
        <div>
          <h2 className="text-4xl font-bold text-sky-700">Query the Google Ads Search API</h2>
          Now you have everything you need to make your first call to the Google Ads API.  This API lets you make SQL like queries
          over a REST like API.  You will need to learn the schema to make the most of it, but it's straightforward if you've
          made it this far.

          <div>
            Query Performance and Spend <GaLink path="/api/docs/reporting/criteria-metrics">metrics</GaLink> 

            <p>
              <strong>Note</strong> - You need to remove dashes from your customer ID and manager ID for the query.
              So 123-456-7890 becomes 1234567890.
            </p>

            <Code>
              {`
              POST https://googleads.googleapis.com/v16/customers/[YOUR CUSTOMER ID]/googleAds:searchStream HTTP/1.1
              Content-Type: application/json
              Accept: application/json
              Authorization: Bearer {{ $dotenv GA_ACCESS_TOKEN }}
              linked-customer-id: [YOUR MANAGER ID]
              developer-token: {{ $dotenv GA_DEVELOPER_TOKEN }}
              
              {
                "query" : "SELECT
                            segments.date,
                            metrics.clicks,
                            metrics.conversions,
                            metrics.impressions,
                            metrics.cost_per_all_conversions,
                            metrics.cost_per_conversion,
                            metrics.cost_per_all_conversions
                          FROM keyword_view
                          WHERE segments.date DURING LAST_30_DAYS"
              }
              `}
            </Code>
          </div>
        </div>
      </div>
    </Main>
  )
}

export default HowToIntegrateWithGoogleAdsApi
